import React, { useState, useEffect, useRef } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playVictory } from '../sounds';

const ACCENT = '#FF9500';
const ACCENT_LIGHT = 'rgba(255,149,0,0.12)';
const MUTED = '#c4b5d9';

function Timer({ seconds, warning }) {
  return (
    <div className="timer-container">
      <div className={`timer-ring ${warning ? 'warning' : ''}`}>{seconds}</div>
    </div>
  );
}

function StoryBuilder({ gameState, playerId, sendMessage, error, clearError }) {
  const {
    phase, currentTurn, totalTurns, phaseTimeLeft,
    currentWriterName, isMyTurn, lastSentence, hasSubmitted,
    sentences, hasVoted, votedCount, totalPlayers,
    winnerSentenceIdx, voteCounts, players,
  } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [input, setInput] = useState('');
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [revealCount, setRevealCount] = useState(0);
  const inputRef = useRef(null);
  const isHost = players?.find(p => p.id === playerId)?.isHost;

  useEffect(() => {
    setLocalTimer(phaseTimeLeft || 0);
  }, [phaseTimeLeft, phase]);

  useEffect(() => {
    if (phase === 'results') return;
    const interval = setInterval(() => {
      setLocalTimer(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    setInput('');
    setSelectedSentence(null);
    if (phase === 'reading') {
      setRevealCount(0);
      playPhaseStart();
    } else if (phase === 'results') {
      playVictory();
    } else if (phase === 'writing') {
      playPhaseStart();
      if (isMyTurn) setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [phase, isMyTurn, currentTurn]);

  // Reading: reveal sentences one by one
  useEffect(() => {
    if (phase !== 'reading' || !sentences) return;
    if (revealCount >= sentences.length) return;
    const t = setTimeout(() => setRevealCount(prev => prev + 1), 2500);
    return () => clearTimeout(t);
  }, [phase, revealCount, sentences]);

  const submitSentence = () => {
    if (!input.trim()) return;
    playClick();
    sendMessage('STORY_SUBMIT', { text: input.trim() });
    setInput('');
  };

  // ===== WRITING PHASE =====
  if (phase === 'writing') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <p style={{ fontSize: '0.8rem', color: MUTED }}>סיבוב {currentTurn} מתוך {totalTurns}</p>
        </div>

        {isMyTurn ? (
          <>
            <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '16px', color: ACCENT }}>
              התור שלך! ✍️
            </h2>

            <div style={{
              padding: '16px', borderRadius: '14px', marginBottom: '16px',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
              borderRight: `4px solid ${ACCENT}`,
            }}>
              <p style={{ fontSize: '0.75rem', color: MUTED, marginBottom: '6px' }}>המשפט הקודם:</p>
              <p style={{ fontSize: '1rem', lineHeight: 1.6 }}>{lastSentence}</p>
            </div>

            {!hasSubmitted ? (
              <>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="כתבו את המשפט הבא בסיפור..."
                  maxLength={200}
                  style={{
                    width: '100%', minHeight: '80px', padding: '14px', borderRadius: '14px',
                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
                    color: 'white', fontSize: '1rem', fontFamily: 'Heebo, sans-serif',
                    resize: 'none', direction: 'rtl', marginBottom: '8px', boxSizing: 'border-box',
                  }}
                />
                <p style={{ textAlign: 'left', fontSize: '0.7rem', color: MUTED, marginBottom: '10px' }}>
                  {input.length}/200
                </p>
                <button className="btn btn-primary" onClick={submitSentence} disabled={!input.trim()}
                  style={{ background: ACCENT, borderColor: ACCENT }}>
                  שלח ▶
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(76,175,80,0.1)', borderRadius: '14px' }}>
                <p style={{ color: '#4CAF50', fontWeight: 700 }}>✓ נשלח!</p>
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '30px 20px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✍️</div>
            <h2 style={{ fontSize: '1.2rem', marginBottom: '8px' }}>
              <span style={{ color: ACCENT }}>{currentWriterName}</span> כותב/ת...
            </h2>
            <p style={{ color: MUTED, fontSize: '0.85rem' }}>ממתינים למשפט הבא בסיפור</p>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== READING PHASE =====
  if (phase === 'reading') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px', color: ACCENT }}>
          📖 הסיפור המלא!
        </h2>

        <div style={{
          padding: '20px', borderRadius: '16px', marginBottom: '16px',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
          lineHeight: 1.8, fontSize: '1rem',
        }}>
          {sentences && sentences.slice(0, revealCount).map((s, i) => (
            <span key={i} style={{
              display: 'inline',
              color: i === 0 ? MUTED : 'white',
              fontWeight: i === 0 ? 400 : 500,
              animation: 'fadeIn 0.5s ease',
            }}>
              {s.text}{' '}
            </span>
          ))}
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
      </div>
    );
  }

  // ===== VOTING PHASE =====
  if (phase === 'voting') {
    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <Timer seconds={localTimer} warning={localTimer <= 5} />

        <h2 style={{ textAlign: 'center', fontSize: '1.2rem', marginBottom: '16px' }}>
          🏆 הצביעו למשפט הכי מצחיק!
        </h2>

        {!hasVoted ? (
          <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
            {sentences && sentences.slice(1).map((s, i) => (
              s.playerId !== playerId && (
                <button
                  key={i}
                  onClick={() => { setSelectedSentence(i + 1); playClick(); }}
                  style={{
                    padding: '12px 16px', borderRadius: '14px', textAlign: 'right',
                    border: selectedSentence === i + 1 ? `2px solid ${ACCENT}` : '2px solid rgba(255,255,255,0.1)',
                    background: selectedSentence === i + 1 ? ACCENT_LIGHT : 'rgba(255,255,255,0.04)',
                    color: 'white', fontFamily: 'Heebo, sans-serif', cursor: 'pointer',
                    fontSize: '0.9rem', lineHeight: 1.5, width: '100%', direction: 'rtl',
                  }}
                >
                  <span style={{ fontSize: '0.7rem', color: MUTED }}>{s.playerName}: </span>
                  {s.text}
                </button>
              )
            ))}
            <button className="btn btn-primary" onClick={() => {
              if (selectedSentence !== null) sendMessage('STORY_VOTE', { sentenceIndex: selectedSentence });
            }} disabled={selectedSentence === null}
              style={{ background: ACCENT, borderColor: ACCENT, marginTop: '8px' }}>
              הצבע!
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(76,175,80,0.1)', borderRadius: '14px' }}>
            <p style={{ color: '#4CAF50', fontWeight: 700 }}>✓ הצבעת!</p>
            <p style={{ color: MUTED, fontSize: '0.85rem', marginTop: '8px' }}>{votedCount} מתוך {totalPlayers} הצביעו</p>
          </div>
        )}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  // ===== RESULTS PHASE =====
  if (phase === 'results') {
    const winnerSentence = sentences && winnerSentenceIdx !== null ? sentences[winnerSentenceIdx] : null;
    const sortedPlayers = players ? [...players].sort((a, b) => b.score - a.score) : [];

    return (
      <div className="card" style={{ direction: 'rtl' }}>
        <h2 style={{ textAlign: 'center', fontSize: '1.3rem', marginBottom: '20px' }}>
          📖 תוצאות!
        </h2>

        {/* Full story */}
        <div style={{
          padding: '16px', borderRadius: '14px', marginBottom: '20px',
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
          lineHeight: 1.8, fontSize: '0.95rem',
        }}>
          {sentences && sentences.map((s, i) => (
            <span key={i} style={{
              color: i === winnerSentenceIdx ? ACCENT : (i === 0 ? MUTED : 'white'),
              fontWeight: i === winnerSentenceIdx ? 700 : (i === 0 ? 400 : 500),
              textDecoration: i === winnerSentenceIdx ? 'underline' : 'none',
            }}>
              {s.text}{' '}
            </span>
          ))}
        </div>

        {/* Winner sentence */}
        {winnerSentence && (
          <div style={{
            textAlign: 'center', padding: '16px', marginBottom: '20px',
            background: ACCENT_LIGHT, borderRadius: '14px', border: `1px solid ${ACCENT}40`,
          }}>
            <p style={{ fontSize: '0.8rem', color: MUTED, marginBottom: '6px' }}>🏆 המשפט המנצח</p>
            <p style={{ fontSize: '1rem', fontWeight: 700 }}>"{winnerSentence.text}"</p>
            <p style={{ fontSize: '0.85rem', color: ACCENT, marginTop: '4px' }}>— {winnerSentence.playerName}</p>
          </div>
        )}

        {/* Leaderboard */}
        <div className="result-section" style={{ marginBottom: '20px' }}>
          <h3>טבלת ניקוד</h3>
          <div className="leaderboard">
            {sortedPlayers.map((p, i) => (
              <div key={p.id} className="leaderboard-item">
                <span className="leaderboard-rank">
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}.`}
                </span>
                <span className="leaderboard-name">
                  {p.avatar && <span style={{ marginLeft: '4px' }}>{p.avatar}</span>} {p.name}
                  {p.id === playerId && <span className="me-badge"> (אני)</span>}
                </span>
                <span className="leaderboard-score">{p.score}</span>
              </div>
            ))}
          </div>
        </div>

        {isHost && (
          <>
            <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }}
              style={{ width: '100%', background: ACCENT, borderColor: ACCENT, marginBottom: '8px' }}>
              סיפור חדש! 📖
            </button>
            <button className="btn btn-secondary" onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}
              style={{ width: '100%' }}>
              חזרה ללובי
            </button>
          </>
        )}
        {!isHost && <p className="waiting">ממתינים שהמנהל ימשיך...</p>}

        {error && <p className="error-msg">{error}</p>}
      </div>
    );
  }

  return <div className="card"><p style={{ textAlign: 'center', color: MUTED }}>טוען...</p></div>;
}

export default StoryBuilder;
